import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import {
  daoTransactionAdyenFinalizePost,
  daoTransactionAdyenPreparePost,
  daoTransactionAdyenUseCcPayPost,
  daoTransactionAdyenUseCcPaySCAPost,
  daoTransactionCcPreparePost,
  daoTransactionUseCcPayPost,
  daoTransactionUseCcPayScaPost,
} from 'dao/transactions-cc-dao';
import { Action } from 'types/action';
import { Thunk } from 'types/root';
import {
  FinalizeAdyenPayload,
  FinalizeAdyenResponse,
  PrepareAdyenPayload,
  PrepareAdyenResp,
  PreparePayload,
  PrepareResp,
  TransactionsCcState,
  UseAdyenCCPayPayload,
  UseAdyenCCPayResp,
  UseAdyenCCPaySCAPayload,
  UseCcPayPayload,
  UseCcPayResp,
} from 'types/transactions-cc';

// Prepare Transaction
export const postTransactionPrepare = createAsyncThunk<TransactionsCcState, PreparePayload, Thunk>(
  'transactions/prepare',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionCcPreparePost(accountKey, sessionId, payload);
    return response as TransactionsCcState;
  },
);

// Prepare Adyen Transaction
export const postTransactionAdyenPrepare = createAsyncThunk<PrepareAdyenResp, PrepareAdyenPayload, Thunk>(
  'transactions/adyenPrepare',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const paymentMethod = payload.paymentMethod;
    const data = {
      paymentMethodKey: payload.paymentMethodKey,
      browserLocale: payload.browserLocale,
      returnUrl: payload.returnUrl,
    };
    const response = await daoTransactionAdyenPreparePost(accountKey, sessionId, data, paymentMethod);
    return response;
  },
);

// Finalize Adyen Transaction
export const postTransactionAdyenFinalize = createAsyncThunk<FinalizeAdyenResponse, FinalizeAdyenPayload, Thunk>(
  'transactions/adyenFinalize',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const paymentMethod = payload.paymentMethod;
    const data = {
      checkoutSessionId: payload.checkoutSessionId,
      checkoutSessionResult: payload.checkoutSessionResult,
      setAsDefault: payload.setAsDefault,
    };
    const response = await daoTransactionAdyenFinalizePost(accountKey, sessionId, data, paymentMethod);
    return response;
  },
);

// Use Credit Card to Pay
export const postTransactionUseCcPay = createAsyncThunk<TransactionsCcState, UseCcPayPayload, Thunk>(
  'transactions/useCcPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionUseCcPayPost(accountKey, sessionId, payload);
    return response as TransactionsCcState;
  },
);

export const postTransactionUseCcScaPay = createAsyncThunk<TransactionsCcState, UseCcPayPayload, Thunk>(
  'transactions/useCcScaPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionUseCcPayScaPost(accountKey, sessionId, payload);
    return response as TransactionsCcState;
  },
);

// Use Adyen's stored credit card to pay
export const postTransactionAdyenUseCcPay = createAsyncThunk<UseAdyenCCPayResp, UseAdyenCCPayPayload, Thunk>(
  'transactions/adyenUseCcPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAdyenUseCcPayPost(accountKey, sessionId, payload);
    return response;
  },
);

export const postTransactionAdyenUseCcScaPay = createAsyncThunk<UseAdyenCCPayResp, UseAdyenCCPaySCAPayload, Thunk>(
  'transactions/adyenUseCcScaPay',
  async (payload, thunkAPI) => {
    const state = thunkAPI.getState();
    const {
      globalWrapper: { accountKey, sessionId },
    } = state;
    const response = await daoTransactionAdyenUseCcPaySCAPost(accountKey, sessionId, payload);
    return response;
  },
);

const initialState: TransactionsCcState = {
  isLoading: false,
  prepareResp: {} as PrepareResp,
  prepareAdyenResp: {} as PrepareAdyenResp,
  useCcPayResp: {} as UseCcPayResp,
};

const transactionsCcSlice = createSlice({
  name: 'transactions-cc',
  initialState,
  reducers: {},

  extraReducers: (builder) => {
    builder
      // Prepare case
      .addCase(postTransactionPrepare.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionPrepare.fulfilled, (state, action: Action) => {
        state.isLoading = false;
        state.prepareResp = action.payload;
      })
      .addCase(postTransactionPrepare.rejected, (state) => {
        state.isLoading = false;
        state.prepareResp = {} as PrepareResp;
      })
      .addCase(postTransactionAdyenPrepare.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionAdyenPrepare.fulfilled, (state, action: Action) => {
        state.isLoading = false;
        state.prepareAdyenResp = action.payload;
      })
      .addCase(postTransactionAdyenPrepare.rejected, (state) => {
        state.isLoading = false;
        state.prepareAdyenResp = {} as PrepareAdyenResp;
      })
      // Use Credit Card to Pay case
      .addCase(postTransactionUseCcPay.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionUseCcPay.fulfilled, (state, action: Action) => {
        state.isLoading = false;
        state.useCcPayResp = action.payload;
      })
      .addCase(postTransactionUseCcPay.rejected, (state) => {
        state.isLoading = false;
        state.useCcPayResp = {} as UseCcPayResp;
      })
      // Use Credit Card to Pay SCA case
      .addCase(postTransactionUseCcScaPay.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(postTransactionUseCcScaPay.fulfilled, (state, action: Action) => {
        state.isLoading = false;
        state.useCcPayResp = action.payload;
      })
      .addCase(postTransactionUseCcScaPay.rejected, (state) => {
        state.isLoading = false;
        state.useCcPayResp = {} as UseCcPayResp;
      });
  },
});

export default transactionsCcSlice.reducer;
